import { AnimatePresence, motion } from 'framer-motion'
import {
  Bell, CalendarDays, ChevronRight, CircleDollarSign, CreditCard, Home, Menu, MessageCircleMore, Plus, ReceiptText, Search, Settings, Users, UserRoundCog, X, } from 'lucide-react'
import { useMemo, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useAuth } from '../context/AuthContext'
import { useWorkspace } from '../context/WorkspaceContext'
import { relative } from '../lib/format'
import { supabase } from '../lib/supabase'
import { T, translateStoredText, useI18n } from '../lib/i18n'
import { Logo } from './Logo'
import { IdentityImage } from './IdentityImage'

const businessNav = [
  { to: '/', key: 'dashboard', icon: Home },
  { to: '/schedule', key: 'schedule', icon: CalendarDays },
  { to: '/clients', key: 'clients', icon: Users },
  { to: '/messages', key: 'messages', icon: MessageCircleMore },
  { to: '/team', key: 'team', icon: UserRoundCog },
  { to: '/finance', key: 'finance', icon: CircleDollarSign },
  { to: '/plans', key: 'plans', icon: CreditCard },
]

const clientNav = [
  { to: '/', key: 'home', icon: Home },
  { to: '/messages', key: 'messages', icon: MessageCircleMore },
  { to: '/invoices', key: 'invoices', icon: ReceiptText },
]

const employeeNav = [
  { to: '/', key: 'my work', icon: Home },
  { to: '/schedule', key: 'my schedule', icon: CalendarDays },
]

export function AppShell() {
  const { user } = useAuth()
  const workspace = useWorkspace()
  const { locale, t } = useI18n()
  const navigate = useNavigate()
  const location = useLocation()
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const nav = workspace.accountKind === 'business'
    ? workspace.isOwner ? businessNav : employeeNav
    : clientNav
  const audience = workspace.accountKind
  const visibleNotifications = useMemo(
    () => workspace.notifications.filter((item) => item.audience === audience && (!workspace.activeClientId || item.client_id === workspace.activeClientId)),
    [audience, workspace.activeClientId, workspace.notifications],
  )
  const unreadCount = visibleNotifications.filter((item) => !item.is_read).length
  const currentNav = location.pathname === '/settings'
    ? 'settings'
    : nav.find((item) => item.to === location.pathname)?.key ?? (workspace.accountKind === 'client' ? 'home' : 'dashboard')
  const activeClient = workspace.activeClientId ? workspace.clientFor(workspace.activeClientId) : undefined
  const identityName = workspace.accountKind === 'business'
    ? workspace.organization?.display_name ?? 'TidyPing'
    : activeClient?.display_name ?? user?.email ?? t('Client')
  const identityPath = workspace.accountKind === 'business' ? workspace.organization?.logo_path : activeClient?.avatar_path
  const identityVersion = workspace.accountKind === 'business' ? workspace.organization?.updated_at : activeClient?.updated_at

  async function markNotificationsRead() {
    const ids = visibleNotifications.filter((item) => !item.is_read).map((item) => item.id)
    if (!ids.length) return
    const { error } = await supabase.from('app_notifications').update({ is_read: true }).in('id', ids)
    if (error) toast.error(t(error.message))
    else await workspace.refresh(true)
  }

  function openNotifications() {
    setNotificationsOpen(true)
    void markNotificationsRead()
  }

  return (
    <div className="app-shell">
      <aside className={`sidebar ${mobileOpen ? 'sidebar--open' : ''}`}>
        <div className="sidebar__brand">
          <Logo inverse />
          <button className="icon-button sidebar__close" onClick={() => setMobileOpen(false)} aria-label={t('Close navigation')}><X size={20} /></button>
        </div>

        <nav className="sidebar__nav" aria-label={t('Primary navigation')}>
          <span className="sidebar__label"><T>{"Workspace"}</T></span>
          {nav.map(({ to, key, icon: Icon }) => (
            <NavLink
              end={to === '/'}
              key={to}
              to={to}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) => `sidebar__link ${isActive ? 'sidebar__link--active' : ''}`}
            >
              <Icon size={19} />
              <span>{t(key)}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar__footer">
          <NavLink to="/settings" className={({ isActive }) => `sidebar__link ${isActive ? 'sidebar__link--active' : ''}`}>
            <Settings size={19} /><span>{t('settings')}</span>
          </NavLink>
          <div className="workspace-chip">
            <IdentityImage path={identityPath} version={identityVersion} name={identityName} kind={workspace.accountKind} size={31} />
            <div>
              <strong>{identityName}</strong>
              <span>{user?.email}</span>
            </div>
          </div>
        </div>
      </aside>

      {mobileOpen && <button className="mobile-scrim" aria-label={t('Close navigation')} onClick={() => setMobileOpen(false)} />}

      <main className="app-main">
        <header className="topbar">
          <div className="topbar__left">
            <button className="icon-button topbar__menu" onClick={() => setMobileOpen(true)} aria-label={t('Open navigation')}><Menu size={21} /></button>
            <div>
              <span className="topbar__eyebrow">{workspace.organization?.display_name ?? 'TidyPing'}</span>
              <strong>{t(currentNav)}</strong>
            </div>
          </div>
          <div className="topbar__actions">
            {(workspace.accountKind === 'client' || workspace.isOwner) && <button className="search-button" type="button" onClick={() => navigate(workspace.accountKind === 'business' ? '/clients?focus=search' : '/messages')}>
              <Search size={17} /><span>{t('search')}</span><kbd><T>{"⌘ K"}</T></kbd>
            </button>}
            {workspace.isOwner && (
              <button className="button button--primary button--compact" onClick={() => navigate('/schedule?new=1')}>
                <Plus size={17} />{t('newCleaning')}
              </button>
            )}
            <button className="icon-button notification-button" onClick={openNotifications} aria-label={t('{count} unread notifications', { count: unreadCount })}>
              <Bell size={20} />
              {unreadCount > 0 && <span>{unreadCount > 9 ? '9+' : unreadCount}</span>}
            </button>
          </div>
        </header>

        <div className="page-container">
          <Outlet />
        </div>
      </main>

      <nav className="mobile-nav" aria-label={t('Mobile navigation')}>
        {nav.slice(0, 5).map(({ to, key, icon: Icon }) => (
          <NavLink end={to === '/'} key={to} to={to} className={({ isActive }) => isActive ? 'active' : ''}>
            <Icon size={20} /><span>{t(key)}</span>
          </NavLink>
        ))}
      </nav>

      <AnimatePresence>
        {notificationsOpen && (
          <>
            <motion.button className="drawer-scrim" aria-label={t('Close notifications')} onClick={() => setNotificationsOpen(false)} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} />
            <motion.aside className="notification-drawer" initial={{ x: 420 }} animate={{ x: 0 }} exit={{ x: 420 }} transition={{ type: 'spring', damping: 28, stiffness: 320 }}>
              <header>
                <div><span className="eyebrow"><T>{"Updates"}</T></span><h2>{t('notifications')}</h2></div>
                <button className="icon-button" onClick={() => setNotificationsOpen(false)} aria-label={t('Close')}><X size={20} /></button>
              </header>
              <div className="notification-list">
                {visibleNotifications.length === 0 ? (
                  <div className="notification-empty"><Bell size={24} /><strong><T>{"You’re all caught up"}</T></strong><span><T>{"Schedule and message updates will appear here."}</T></span></div>
                ) : visibleNotifications.map((item) => (
                  <button
                    key={item.id}
                    className={`notification-row ${item.is_read ? '' : 'notification-row--unread'}`}
                    onClick={() => {
                      setNotificationsOpen(false)
                      navigate(item.appointment_id
                        ? (workspace.accountKind === 'business' ? `/schedule?appointment=${item.appointment_id}` : '/')
                        : workspace.isOwner || workspace.accountKind === 'client' ? '/messages' : '/')
                    }}
                  >
                    <div className="notification-row__dot" />
                    <div><strong>{translateStoredText(t, locale, item.title)}</strong><p>{item.message_id || item.conversation_id || item.title.startsWith('New message from ') ? (/^Sent (?:a|\d+) photo(?:s)? or video(?:s)?\.$/.test(item.body) ? translateStoredText(t, locale, item.body) : item.body) : translateStoredText(t, locale, item.body)}</p><span>{relative(item.created_at)}</span></div>
                    <ChevronRight size={16} />
                  </button>
                ))}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}
